/**
 * StatusNotice — the inline notice for a non-`ok` answer status (abstenu / disabled / error). Combines
 * a status glyph + a titled message (color + glyph + text, never color-only). `ok` renders nothing
 * (the answer text speaks for itself). Used inside an assistant turn to make the governance posture
 * unmistakable: a clinical request was refused (abstenu), or the assistant is off/unavailable.
 */
import { useT } from "@/app";
import { statusVisual } from "../lib";
import type { AnswerStatus } from "../types";

export interface StatusNoticeProps {
  status: AnswerStatus;
}

const NOTICE_KEYS: Record<Exclude<AnswerStatus, "ok">, { title: string; desc: string; tint: string }> = {
  abstenu: {
    title: "ai.assistant.abstenu.title",
    desc: "ai.assistant.abstenu.desc",
    tint: "var(--nv-tint-high)",
  },
  disabled: {
    title: "ai.assistant.disabled.title",
    desc: "ai.assistant.disabled.desc",
    tint: "var(--nv-surface-2)",
  },
  error: {
    title: "ai.assistant.error.title",
    desc: "ai.assistant.error.desc",
    tint: "var(--nv-tint-critical)",
  },
};

export function StatusNotice({ status }: StatusNoticeProps) {
  const { t } = useT();
  if (status === "ok") return null;
  const v = statusVisual(status);
  const keys = NOTICE_KEYS[status];
  return (
    <div
      role="status"
      style={{
        display: "flex",
        gap: "var(--nv-s-2)",
        alignItems: "flex-start",
        padding: "var(--nv-s-2) var(--nv-s-3)",
        borderRadius: "var(--nv-r-sm)",
        background: keys.tint,
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      <span aria-hidden style={{ fontSize: "var(--nv-fs-body)" }}>
        {v.glyph}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
        <strong style={{ fontSize: "var(--nv-fs-label)" }}>{t(keys.title)}</strong>
        <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-slate)" }}>{t(keys.desc)}</span>
      </div>
    </div>
  );
}
